import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * "Test call" on a trunk (plan-008 ST-UN, design §22.14, R-UI-5, AC-UI-3's UI half).
 *
 *   · **The agent calls me** — a voice agent rings the member's own phone through the trunk, from one
 *     of the trunk's caller-ID numbers bound to it. It is a test: "Tests are free." (plan-017 PQ-5, PQ-14 — free
 *     from the talk rate cutover; the owner accepted the short gap before it, when a legacy test still bills), and
 *     it may ring through a trunk that is not verified yet: an answered call verifies it (DD8-24).
 *   · **I call the agent** — arms one of the trunk's bound numbers; the member rings it from any phone
 *     within two minutes, and the dialog shows whether the conversation arrived, which agent answered
 *     and the codec it used.
 *
 * Progress is read, never assumed: the dialog polls `readSipTrunkTestCall` until the conversation
 * ends, and says "Codec not reported" when the SIP edge did not report one.
 *
 * "The agent calls me" is a dial, so it shares the per-minute limit of "Ring a number now" (review C2):
 * over it, the wait its `Retry-After` named is counted down beside "Ring me", which is held for exactly
 * that long (`./dial-wait.ts`, plan-008 ST-RV2). Every other refusal — a trunk the runtime will not
 * dial through included — is said in the API's own customer-safe sentence.
 *
 * An inbound-only trunk (plan-015 SIP, ADR 0046) cannot dial, so its dialog offers "I call the agent" alone.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Input, Segmented, Select } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import {
  isInboundOnlyTrunkView,
  type SipTrunkTestCallProgressView,
  type SipTrunkTestCallStartedView,
  type SipTrunkView,
} from "../../api/slice/sip-trunk.js";
import { dialRateLimitedSentence, dialWaitOf, useDialWait } from "./dial-wait.js";
import { codecWords, endReasonWords } from "./sip-trunk-copy.js";
import { SIP_TRUNK_QUERY_KEY } from "./sip-trunk-query.js";

export type Direction = "agent_calls_me" | "i_call_the_agent";

/** How often the dialog asks for progress while a test is running. */
const PROGRESS_POLL_MS = 2_000;

interface Started {
  readonly phoneNumberId: string;
  readonly since: string;
  readonly answer: SipTrunkTestCallStartedView;
}

/** `2 min 14 s`, `45 s` — the conversation's billed length. */
export function durationWords(second: number): string {
  const minute = Math.floor(second / 60);
  const rest = second % 60;
  return minute === 0 ? portalText("operations", "value_s_09263e7c", { v0: String(String(rest)) }) : portalText("operations", "value_min_value_s_7a6246d3", { v0: String(String(minute)), v1: String(String(rest)) });
}

/** The state of a running test, in plain words; the codec only once the leg was answered. */
export function progressWords(progress: SipTrunkTestCallProgressView, direction: Direction, voiceAgentName: string | null): string {
  switch (progress.state) {
    case "idle":
      return direction === "i_call_the_agent" ? portalText("operations", "waiting_for_your_call_4bcd64b1") : portalText("operations", "starting_e5f58095");
    case "dialing":
      return portalText("operations", "ringing_417814b1");
    case "in_progress":
      return portalText("operations", "answeredvalue_value_f1304d89", { v0: String(voiceAgentName === null ? "" : portalText("operations", "by_value_e840584a", { v0: String(voiceAgentName) })), v1: String(codecWords(progress.codec)) });
    case "ended": {
      const length = progress.billedSecond === null ? "" : portalText("operations", "after_value_4b46dc65", { v0: String(durationWords(progress.billedSecond)) });
      const reason = progress.endReason === null ? "" : ` · ${endReasonWords(progress.endReason)}`;
      return portalText("operations", "endedvaluevalue_value_46f34686", { v0: String(length), v1: String(reason), v2: String(codecWords(progress.codec)) });
    }
  }
}

export interface SipTrunkTestCallDialogProps {
  readonly trunk: SipTrunkView;
  readonly onClose: () => void;
  /**
   * numbers-sip-sync: opened from one number's row — that number is chosen in both directions where it can be, and
   * "Call me" opens on "The agent calls me". Absent: the trunk's first caller-ID and bound numbers, as before.
   */
  readonly phoneNumberId?: string;
  readonly direction?: Direction;
}

export function SipTrunkTestCallDialog({ trunk, onClose, phoneNumberId, direction: openOn }: SipTrunkTestCallDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const inboundOnly = isInboundOnlyTrunkView(trunk);
  // An inbound-only trunk has nothing to call out from (and its numbers present no caller ID).
  const callerIdNumber = inboundOnly ? [] : trunk.number.filter((one) => one.isCallerId && one.voiceAgentId !== null);
  const boundNumber = trunk.number.filter((one) => one.voiceAgentId !== null);
  const [direction, setDirection] = useState<Direction>(
    openOn === "agent_calls_me" && callerIdNumber.length > 0
      ? "agent_calls_me"
      : openOn === "i_call_the_agent"
        ? "i_call_the_agent"
        : callerIdNumber.length > 0
          ? "agent_calls_me"
          : "i_call_the_agent",
  );
  const preferred = (numbers: typeof boundNumber): string =>
    (numbers.find((one) => one.phoneNumberId === phoneNumberId) ?? numbers[0])?.phoneNumberId ?? "";
  const [fromId, setFromId] = useState(preferred(callerIdNumber));
  const [ringId, setRingId] = useState(preferred(boundNumber));
  const [partyNumber, setPartyNumber] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [started, setStarted] = useState<Started | null>(null);
  const wait = useDialWait();

  const start = useMutation({
    mutationFn: async (): Promise<Started> => {
      const since = new Date().toISOString();
      if (direction === "agent_calls_me") {
        const from = callerIdNumber.find((one) => one.phoneNumberId === fromId);
        if (from === undefined || from.voiceAgentId === null) throw new Error(portalText("operations", "choose_the_number_to_call_from_933bfcfa"));
        const answer = await gateway.createSipTrunkTestCall(trunk.sipTrunkId, {
          direction,
          voiceAgentId: from.voiceAgentId,
          callerIdPhoneNumberId: from.phoneNumberId,
          partyNumber: partyNumber.trim(),
        });
        return { phoneNumberId: from.phoneNumberId, since, answer };
      }
      const ring = boundNumber.find((one) => one.phoneNumberId === ringId);
      if (ring === undefined) throw new Error(portalText("operations", "choose_the_number_you_will_ring_f025e462"));
      const answer = await gateway.createSipTrunkTestCall(trunk.sipTrunkId, { direction, phoneNumberId: ring.phoneNumberId });
      return { phoneNumberId: ring.phoneNumberId, since, answer };
    },
    onSuccess: (made) => {
      setStarted(made);
    },
    onError: (raised: unknown) => {
      const second = dialWaitOf(raised);
      if (second === null) {
        setProblem(() => (apiErrorText(raised, portalText("operations", "the_test_call_could_not_be_started_nothing_was_dialled_c3bb8e10"))));
      } else {
        setProblem(null);
        wait.start(second);
      }
    },
  });

  const progress = useQuery({
    queryKey: ["sip-trunk-test-call", trunk.sipTrunkId, started?.phoneNumberId ?? "", started?.since ?? ""],
    queryFn: () => {
      if (started === null) throw new Error(portalText("operations", "no_test_call_is_running_7e12fb62"));
      return gateway.readSipTrunkTestCall(trunk.sipTrunkId, started.phoneNumberId, started.since);
    },
    enabled: started !== null,
    refetchInterval: (query) => (query.state.data?.state === "ended" ? false : PROGRESS_POLL_MS),
  });

  const end = useMutation({
    mutationFn: () => {
      if (started === null || started.answer.kind !== "dialing") return Promise.resolve();
      return gateway.endSipTrunkTestCall(trunk.sipTrunkId, started.answer.conversationId);
    },
    onSettled: async () => {
      await progress.refetch();
    },
  });

  const close = (): void => {
    void queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk });
    void queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunkEvent });
    onClose();
  };

  const answeredBy =
    progress.data?.voiceAgentId === null || progress.data?.voiceAgentId === undefined
      ? null
      : (trunk.number.find((one) => one.voiceAgentId === progress.data?.voiceAgentId)?.voiceAgentName ?? null);
  const canHangUp = started !== null && started.answer.kind === "dialing" && progress.data?.state !== "ended";
  const armedNumber =
    started?.answer.kind === "armed" ? trunk.number.find((one) => one.phoneNumberId === started.phoneNumberId) : undefined;
  const expectUntil =
    started?.answer.kind === "armed"
      ? new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { hour: "2-digit", minute: "2-digit" }).format(new Date(started.answer.expectUntil))
      : null;

  // Only "The agent calls me" dials, so only it waits out the per-minute limit.
  const waitSecond = direction === "agent_calls_me" ? wait.remainingSecond : null;

  const unavailable =
    direction === "agent_calls_me"
      ? callerIdNumber.length === 0
        ? portalText("operations", "mark_one_of_this_trunk_s_numbers_use_as_caller_id_and_conn_9148b997")
        : null
      : boundNumber.length === 0
        ? portalText("operations", "connect_one_of_this_trunk_s_numbers_to_an_agent_first_487fc80b")
        : null;

  return (
    <Dialog
      open
      title={portalText("operations", "test_call_through_value_92710448", { v0: String(trunk.displayName) })}
      onClose={close}
      actions={
        <>
          {canHangUp ? (
            <Button
              variant="secondary"
              disabled={end.isPending}
              onClick={() => {
                end.mutate();
              }}
            >
              {portalText("operations", "hang_up_66be99b5")}</Button>
          ) : null}
          <Button variant="secondary" onClick={close}>
            {portalText("operations", "close_bbfa773e")}</Button>
          {started === null ? (
            <Button
              variant="primary"
              disabled={start.isPending || unavailable !== null || waitSecond !== null}
              onClick={() => {
                setProblem(null);
                start.mutate();
              }}
            >
              {direction === "agent_calls_me" ? portalText("operations", "ring_me_6c96f739") : portalText("operations", "start_the_test_946ac318")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="nb-form">
        {started === null ? (
          <>
            {inboundOnly ? null : (
              <Segmented
                name="sip-trunk-test-direction"
                legend={portalText("operations", "which_way_41e970e1")}
                value={direction}
                onChange={(next) => {
                  setDirection(next);
                  setProblem(null);
                }}
                options={[
                  { value: "agent_calls_me", label: "The agent calls me" },
                  { value: "i_call_the_agent", label: "I call the agent" },
                ]}
              />
            )}
            {unavailable === null ? null : <p className="c-note">{unavailable}</p>}
            {direction === "agent_calls_me" && unavailable === null ? (
              <>
                <Field label={portalText("operations", "call_from_9047baac")}>
                  {(control) => (
                    <Select
                      {...control}
                      value={fromId}
                      onChange={(event) => {
                        setFromId(event.currentTarget.value);
                      }}
                    >
                      {callerIdNumber.map((one) => (
                        <option key={one.phoneNumberId} value={one.phoneNumberId}>
                          {one.e164} · {one.voiceAgentName ?? portalText("operations", "an_agent_14ff0384")}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={portalText("operations", "your_phone_number_303afa01")} description={portalText("operations", "in_international_form_with_like_306912345678_3b5faf19")}>
                  {(control) => (
                    <Input
                      {...control}
                      font="mono"
                      inputMode="tel"
                      value={partyNumber}
                      onChange={(event) => {
                        setPartyNumber(event.currentTarget.value);
                      }}
                    />
                  )}
                </Field>
                <p className="c-note">{portalText("core", "talk_rate.tests_are_free")}</p>
              </>
            ) : null}
            {direction === "i_call_the_agent" && unavailable === null ? (
              <>
                <Field label={portalText("operations", "the_number_you_will_ring_3e8ae528")}>
                  {(control) => (
                    <Select
                      {...control}
                      value={ringId}
                      onChange={(event) => {
                        setRingId(event.currentTarget.value);
                      }}
                    >
                      {boundNumber.map((one) => (
                        <option key={one.phoneNumberId} value={one.phoneNumberId}>
                          {one.e164} · {one.voiceAgentName ?? portalText("operations", "an_agent_14ff0384")}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <p className="c-note">
                  {portalText("operations", "ring_it_from_any_phone_within_two_minutes_the_conversation_7847b860")}</p>
              </>
            ) : null}
          </>
        ) : (
          <>
            {armedNumber === undefined ? null : (
              <p className="c-note">
                {portalText("operations", "ring_e00c622b")}{armedNumber.e164} {portalText("operations", "from_any_phone_before_95d30774")}{expectUntil}.
              </p>
            )}
            <p className="nb-progress" role="status">
              {progress.data === undefined
                ? portalText("operations", "starting_e5f58095")
                : progressWords(progress.data, direction, answeredBy)}
            </p>
          </>
        )}
        {problem === null ? null : (
          <p className="c-note c-note-critical" role="alert">
            {problem}
          </p>
        )}
        {started === null && waitSecond !== null ? (
          <p className="c-note c-note-critical" role="alert">
            {dialRateLimitedSentence(waitSecond)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
